import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useResizingHeight } from '@atlaskit/motion/height';
import { Box } from '@atlaskit/primitives/compiled/box';
import Suspense from '@atlaskit/react-ufo/suspense';
import type { InvitePeopleData } from '@atlassian/invite-people/types';
import { graphql, useLazyLoadQuery } from 'react-relay';
// eslint-disable-next-line @atlassian/data/use-single-relay-environment
import { loadQuery, type PreloadedQuery } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';
import { ErrorBoundary } from '@townsquare/monitoring/error-boundary';
import type { ErrorBoundaryFallbackProps } from '@townsquare/monitoring/types';
import {
	getAggRelayEnvironment,
	AggRelayEnvironmentProvider,
} from '@townsquare/relay-utils/agg-environment';
import { useUserStore } from '@townsquare/user-store';

import { Skeleton } from '../components/SpaceInvitePeople/Skeleton';
import {
	SpaceInviteInput,
	SpaceInviteInputProps,
} from '../components/SpaceInvitePeople/SpaceInviteInput';
import RolePickerQuery, {
	type RolePickerWithPermissionQuery,
} from '../components/SpaceInvitePeople/__generated__/RolePickerWithPermissionQuery.graphql';
import {
	JiraRoleDetail,
	JiraSpaceOption,
	useSpaceInviteInputReturnType,
} from '../types/SpaceInputTypes';
import { getIsAdminFromStore } from '../utils/getIsAdminFromStore';
import { hasJiraProducts } from '../utils/hasJiraProducts';

import type { useSpaceInviteInputQuery } from './__generated__/useSpaceInviteInputQuery.graphql';
import { mapOfferingNameToEdition } from './mapOfferingNameToEdition';

type SpaceInviteInputWithDataProps = Omit<SpaceInviteInputProps, 'appEdition' | 'queryRef'>;

const SpaceInviteInputWithData = ({ cloudId, ...props }: SpaceInviteInputWithDataProps) => {
	const [currentUser] = useUserStore();
	const data = useLazyLoadQuery<useSpaceInviteInputQuery>(
		graphql`
			query useSpaceInviteInputQuery($cloudId: ID!, $userId: String!, $tenantId: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "jira-software") {
						entitlement {
							offering {
								name
							}
						}
					}
				}
				...SpaceInviteInput_data @arguments(cloudId: $cloudId, userId: $userId, tenantId: $tenantId)
			}
		`,
		{
			cloudId: cloudId,
			userId: currentUser.accountId,
			tenantId: cloudId,
		},
		{ fetchPolicy: 'store-or-network' },
	);

	const offering = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.offering?.name;
	const appEdition = useMemo(() => mapOfferingNameToEdition(offering), [offering]);

	return <SpaceInviteInput {...props} appEdition={appEdition} cloudId={cloudId} queryRef={data} />;
};

const ErrorView = ({
	cloudId,
	selectedSpace,
	selectedRole,
	error,
}: {
	cloudId: string;
	error: Error;
	selectedSpace: JiraSpaceOption | undefined;
	selectedRole: JiraRoleDetail | undefined;
}) => {
	const analytics = useAnalytics();
	useEffect(() => {
		void analytics.track('spacePicker', 'render-failed', {
			selectedSpace: selectedSpace?.key,
			selectedRole: selectedRole?.id,
			errorMessage: error.message,
		});
	}, [analytics, cloudId, selectedSpace?.key, selectedRole?.id, error.message]);
	return null;
};

const SpaceInviteInputWrapper = (props: SpaceInviteInputWithDataProps) => {
	const resizingHeight = useResizingHeight();

	const SpaceInviteInputFallback = ({ error }: ErrorBoundaryFallbackProps) => (
		<ErrorView
			error={error}
			cloudId={props.cloudId}
			selectedSpace={props.selectedSpace}
			selectedRole={props.selectedRole}
		/>
	);

	return (
		<Box {...resizingHeight}>
			<ErrorBoundary FallbackComponent={SpaceInviteInputFallback}>
				<AggRelayEnvironmentProvider>
					<Suspense
						interactionName="space-invite-input-agg-query"
						fallback={
							<Skeleton
								labelWidth="45%"
								testId="teams.invite-user-modal.space-input.loading-skeleton"
							/>
						}
					>
						<SpaceInviteInputWithData {...props} />
					</Suspense>
				</AggRelayEnvironmentProvider>
			</ErrorBoundary>
		</Box>
	);
};

export const useSpaceInviteInput = ({
	cloudId,
}: {
	cloudId: string | null | undefined;
}): useSpaceInviteInputReturnType => {
	const [selectedSpace, setSelectedSpace] = useState<JiraSpaceOption | undefined>(undefined);
	const [selectedRole, setSelectedRole] = useState<JiraRoleDetail | undefined>(undefined);
	const [permissionAndRolesQueryRef, setPermissionAndRolesQueryRef] =
		useState<PreloadedQuery<RolePickerWithPermissionQuery> | null>(null);

	const onSelectSpace = useCallback(
		(space: JiraSpaceOption | undefined) => {
			setSelectedSpace(space);
			setSelectedRole(undefined);
			if (space && cloudId) {
				const environment = getAggRelayEnvironment();
				setPermissionAndRolesQueryRef(
					loadQuery(
						environment,
						RolePickerQuery,
						{ cloudId, projectKey: space.key },
						{
							fetchPolicy: 'store-or-network',
						},
					),
				);
			}
		},
		[cloudId],
	);

	// https://hello.jira.atlassian.cloud/browse/WAR-5070 implement onValidate when implementing rest apis
	const plugin = useMemo(
		() => ({
			render: (data: InvitePeopleData) => {
				if (!hasJiraProducts(data.selectedProducts) || !cloudId) {
					return null;
				}

				return (
					<SpaceInviteInputWrapper
						selectedSpace={selectedSpace}
						onSelectSpace={onSelectSpace}
						selectedRole={selectedRole}
						setSelectedRole={setSelectedRole}
						cloudId={cloudId}
						permissionAndRolesQueryRef={permissionAndRolesQueryRef ?? null}
					/>
				);
			},
			onValidate: async (data: InvitePeopleData) => {
				if (!hasJiraProducts(data.selectedProducts)) {
					return { isValid: true };
				}

				const isAdmin = getIsAdminFromStore(selectedSpace, cloudId);

				if (isAdmin && !selectedRole) {
					return { isValid: false };
				}

				return { isValid: true };
			},
		}),
		[cloudId, selectedSpace, selectedRole, onSelectSpace, permissionAndRolesQueryRef],
	);

	const data = useMemo(() => ({ selectedSpace, selectedRole }), [selectedSpace, selectedRole]);

	return {
		plugin,
		data,
	};
};
